import { Box } from '../Box';
import type { TypographyVariant } from '@withjoy/joykit/theme';
import { pxToRem, styled } from '@withjoy/joykit/theme';
import { createGlobalStyle } from '@withjoy/joykit/theme';
import { listPresentationCss, LISTS_OPTION_CLASS, LISTS_ENABLED_CLASS } from './listStyles';
import { animationTransition } from '@shared/utils/animationTransition';

interface RichTextEditorOverrideProps {
  Paragraph?: { props: { typographyVariant: TypographyVariant; fontFamily?: string } };
  Heading?: {
    props?: { fontFamily?: string };
    h1?: { props: { fontSize?: string } };
    h2?: { props: { fontSize?: string } };
    h3?: { props: { fontSize?: string } };
    h4?: { props: { fontSize?: string } };
    h5?: { props: { fontSize?: string } };
    h6?: { props: { fontSize?: string } };
  };
}

/* Reset all styles to default, to make effect of styles from rich text editor */
const RichTextEditorGlobalStyle = createGlobalStyle<{ overrides?: RichTextEditorOverrideProps }>`
  .jodit-popup-container h1, .jodit-popup-container h2, .jodit-popup-container h3, .jodit-popup-container h4, .jodit-popup-container h5, .jodit-popup-container h6 {
    all: revert;
  }
  .jodit-editor h1, .jodit-editor h2, .jodit-editor h3, .jodit-editor h4, .jodit-editor h5, .jodit-editor h6 {
    all: revert;
    font-weight: inherit;
    line-height: inherit;
    font-family: ${({ overrides, theme }) => overrides?.Heading?.props?.fontFamily || theme.typography.variants.display4.fontFamily};
  }

  .jodit-editor h1 {
    font-size: ${({ overrides }) => overrides?.Heading?.h1?.props.fontSize || pxToRem(32)};
  }
  .jodit-editor h2 {
    font-size: ${({ overrides }) => overrides?.Heading?.h2?.props.fontSize || pxToRem(24)};
  }
  .jodit-editor h3 {
    font-size: ${({ overrides }) => overrides?.Heading?.h3?.props.fontSize || pxToRem(18.72)};
  }
  .jodit-editor h4 {
    font-size: ${({ overrides }) => overrides?.Heading?.h4?.props.fontSize || pxToRem(16)};
  }
  .jodit-editor h5 {
    font-size: ${({ overrides }) => overrides?.Heading?.h5?.props.fontSize || pxToRem(13.28)};
  }
  .jodit-editor h6 {
    font-size: ${({ overrides }) => overrides?.Heading?.h6?.props.fontSize || pxToRem(10.72)};
  }

  .jodit-popup-container p,
  .jodit-editor p {
    all: revert;
    ${({ overrides, theme }) => overrides?.Paragraph?.props && theme.typography.variants[overrides.Paragraph.props.typographyVariant]};
  }
  .jodit-editor p {
    ${({ overrides }) => (overrides?.Paragraph?.props?.fontFamily ? { fontFamily: overrides.Paragraph.props.fontFamily } : {})};
  }

  span.jodit-placeholder {
    ${({ overrides, theme }) => overrides?.Paragraph?.props && theme.typography.variants[overrides.Paragraph.props.typographyVariant]};
  }

  .jodit-popup-container a,
  .jodit-editor a {
    all: revert;
    color: ${props => props.theme.colors.linkText};
    &:hover {
      color: ${props => props.theme.colors.linkHover};
    }
    &:active {
      color: ${props => props.theme.colors.linkActive};
    }
  }

  .jodit-editor:not(.jodit-popup-container) blockquote {
    all: revert;
    text-align: left;
    border-left: 3px solid ${props => props.theme.colors.mono3};
    margin-left: ${props => props.theme.space[6]};
    padding-left: ${props => props.theme.space[5]};
    ${({ overrides, theme }) => overrides?.Paragraph?.props && theme.typography.variants[overrides.Paragraph.props.typographyVariant]};
    ${({ overrides }) => (overrides?.Paragraph?.props?.fontFamily ? { fontFamily: overrides.Paragraph.props.fontFamily } : {})};
  }

  /* Lists as they are being typed. Scoped to the opt-in class rather than .jodit-editor: this is a
     global stylesheet shared by every editor instance, and the ~15 surfaces on the default toolbar
     have render targets that still don't style lists. See listPresentationCss for the rules. */
  ${listPresentationCss(`.${LISTS_ENABLED_CLASS}`)}

  .jodit-editor:not(.jodit-popup-container) pre {
    text-align: left;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    background-color: ${props => props.theme.colors.mono3};
    border-radius: ${props => props.theme.space[1]};
    padding: ${props => props.theme.space[1]};
  }

  .jodit-popup-container .jodit-toolbar-button__button .jodit-toolbar-button__text {
    font-family: 'Inter UI';
  }

  /* Rows of the combined list dropdown (see listsControl.ts childTemplate).
     Jodit's icon SVGs carry a viewBox but no intrinsic width/height, so they
     need explicit sizing here. */
  .jodit-popup-container .${LISTS_OPTION_CLASS} {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.space[3]};
  }
  .jodit-popup-container .${LISTS_OPTION_CLASS} svg {
    width: ${pxToRem(14)};
    height: ${pxToRem(14)};
    flex-shrink: 0;
  }
`;

type StyledRichTextEditorProps = Readonly<{
  $minHeight?: string;
  $editableAreaMaxHeight?: string;
  $autoGrow?: boolean;
  $autoGrowMaxHeight?: string;
  $toolbarAlign?: 'start' | 'end';
}>;

/* Override editor styles */
const StyledRichTextEditor = styled(Box)<StyledRichTextEditorProps>`
  /* The lightest of the three resting greys and the only field with no hover state at all.
     Now on the shared contract: see doc/field-focus-consistency/PLAN.md. */
  position: relative;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: ${props => props.theme.radii.field};
  transition: ${animationTransition('border-color', 'box-shadow')};

  &::after {
    content: ' ';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${props => props.theme.radii.field};
    box-shadow: 0 0 0 2px transparent;
    pointer-events: none;
    transition: ${animationTransition('box-shadow')};
  }

  &:hover::after {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.mono6};
  }

  /* Declared after :hover, at equal specificity, so focus wins while the pointer rests on it. */
  &[data-focused='true']::after {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.inputBorderFocused};
  }

  div.jodit-add-new-line {
    display: none;
  }
  div.jodit-container {
    border-radius: ${props => props.theme.radii.field};
    background-color: white;
    border: none;
    box-shadow: none;
    text-align: left;
    overflow: overlay;
  }
  &[data-resizable='true'] div.jodit-container {
    border-radius: ${props => props.theme.radii.field} ${props => props.theme.radii.field} 0 0;
  }
  div.jodit-toolbar__box {
    padding: ${props => props.theme.space[5]};
    border-radius: ${props => props.theme.radii.field} ${props => props.theme.radii.field} 0 0;
    border-bottom: 1px solid ${props => props.theme.colors.mono3};
  }
  div.jodit .jodit-workplace .jodit-wysiwyg {
    padding: ${props => props.theme.space[6]};
  }
  div.jodit .jodit-workplace .jodit-wysiwyg > *:first-child {
    margin-top: 0px;
  }
  span.jodit-toolbar-editor-collection .jodit-toolbar-button {
    margin: 0px;
    padding: 0px;
    height: fit-content;
    width: auto;
  }
  button.jodit-toolbar-button__button,
  span.jodit-toolbar-button {
    margin: 0px;
    padding: 0px;
    height: fit-content;
    width: auto;
    min-width: auto;
  }
  .jodit-ui-group {
    align-items: center;
    gap: ${props => props.theme.space[5]};
  }
  .jodit-ui-group_line_true {
    align-items: center;
    justify-content: ${props => (props.$toolbarAlign === 'start' ? 'flex-start' : 'end')};
  }
  div.jodit-toolbar__box:not(:empty) .jodit-toolbar-editor-collection:after {
    background-color: ${props => props.theme.colors.white};
  }
  span.jodit-toolbar-button__trigger:hover:not([disabled]),
  span.jodit-toolbar-button_with-trigger_true:hover:not([disabled]),
  button.jodit-toolbar-button__button[aria-pressed='true']:not([disabled]),
  button.jodit-toolbar-button__button[aria-pressed='true']:hover:not([disabled]),
  button.jodit-toolbar-button__button:hover:not([disabled]) {
    background-color: transparent;
    border-color: transparent;
  }
  span.jodit-toolbar-button__trigger:hover:not([disabled]),
  span.jodit-toolbar-button_with-trigger_true:hover:not([disabled]),
  button.jodit-toolbar-button__button[aria-pressed='true']:hover:not([disabled]),
  button.jodit-toolbar-button__button:hover:not([disabled]) {
    color: ${props => props.theme.colors.linkHover};
    svg {
      fill: ${props => props.theme.colors.linkHover};
    }
  }
  button.jodit-toolbar-button__button[aria-pressed='true']:not([disabled]) {
    color: ${props => props.theme.colors.linkActive};
    svg {
      fill: ${props => props.theme.colors.linkActive};
    }
  }
  .jodit-ui-group__font .jodit-toolbar-button__trigger,
  .jodit-toolbar-button_left .jodit-toolbar-button__trigger {
    display: none;
  }
  /* Override editor heading toolbar styles */
  span.jodit-ui-group__paragraph {
    position: absolute;
    left: 0;
    width: ${pxToRem(100)};
  }
  span.jodit-toolbar-button_underline .jodit-toolbar-button__icon,
  span.jodit-toolbar-button_italic .jodit-toolbar-button__icon,
  span.jodit-toolbar-button_bold .jodit-toolbar-button__icon,
  span.jodit-ui-group__paragraph .jodit-toolbar-button__icon {
    display: none;
  }
  span.jodit-ui-group__paragraph .jodit-toolbar-button__button {
    width: 100%;
    min-width: ${pxToRem(52)};
    ${props => props.theme.typography.variants.button1};
  }
  span.jodit-ui-group__paragraph .jodit-toolbar-button__button .jodit-toolbar-button__text {
    margin-left: 0;
    justify-content: flex-start;
    ${props => props.theme.typography.variants.button1};
  }
  span.jodit-toolbar-button_bold .jodit-toolbar-button__button::before {
    content: 'B';
    ${props => props.theme.typography.variants.button1};
  }
  span.jodit-toolbar-button_italic .jodit-toolbar-button__button::before {
    content: 'I';
    ${props => props.theme.typography.variants.button1};
    width: ${props => props.theme.space[3]};
    font-style: italic;
  }
  span.jodit-toolbar-button_underline .jodit-toolbar-button__button::before {
    content: 'U';
    ${props => props.theme.typography.variants.button1};
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  span.jodit-ui-group__paragraph button.jodit-toolbar-button__button {
    justify-content: flex-start;
  }
  /*
   * The height cap + scroll live on the WORKPLACE wrapper, never on the editable
   * area (.jodit-wysiwyg) itself. When .jodit-wysiwyg is the scroll container,
   * Jodit's scrollIntoViewIfNeeded helper runs \`root.scrollTop = elm.offsetTop\`
   * on input (guarded by root.clientHeight !== root.scrollHeight), which yanks the
   * scroll to the top while the caret stays at the end ("editor scrolls up while
   * typing"). Keeping .jodit-wysiwyg uncapped (clientHeight === scrollHeight) makes
   * that guard false, so the browser follows the caret natively; the workplace
   * still provides the bounded, scrollable viewport (toolbar stays fixed).
   *
   * Selector must out-specify Jodit's own \`.jodit-container:not(.jodit_inline)
   * .jodit-workplace\` rule (max-height:100%; overflow:hidden), hence \`div.jodit-container\`.
   */
  & div.jodit-container .jodit-workplace {
    ${({ $editableAreaMaxHeight, $autoGrow, $autoGrowMaxHeight, $minHeight }) => {
      if ($autoGrow) {
        return $autoGrowMaxHeight ? `max-height: ${$autoGrowMaxHeight}; overflow-y: auto;` : 'max-height: none;';
      }
      const cap = $editableAreaMaxHeight ?? ($minHeight ? `calc(${$minHeight} - ${pxToRem(53)})` : undefined);
      return cap ? `max-height: ${cap}; overflow-y: auto;` : '';
    }}
  }
  /* .jodit-workplace is a flex column and Jodit pins the editable to height:100%
     (specificity 0,3,0) + shrinks it via flex; force it to grow with content so the
     workplace above is the sole scroll container. */
  & div.jodit-container .jodit-workplace .jodit-wysiwyg {
    height: auto !important;
    flex: none !important;
    overflow-y: visible !important;
    min-height: ${({ $minHeight }) => ($minHeight ? `calc(${$minHeight} - ${pxToRem(53)})` : '100%')} !important;
  }
`;

const StyledResizeHandle = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${pxToRem(16)};
  cursor: ns-resize;
  user-select: none;
  touch-action: none;
  background-color: white;
  border-top: 1px solid ${props => props.theme.colors.mono3};
  border-radius: 0 0 ${props => props.theme.space[3]} ${props => props.theme.space[3]};
  &::after {
    content: '';
    width: ${pxToRem(24)};
    height: ${pxToRem(3)};
    border-radius: ${pxToRem(1.5)};
    background-color: ${props => props.theme.colors.mono5};
  }
  &:hover::after {
    background-color: ${props => props.theme.colors.mono8};
  }
`;

export { StyledRichTextEditor, RichTextEditorGlobalStyle, StyledResizeHandle };
export type { RichTextEditorOverrideProps };
